import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Icon from '../Icon.jsx';
import PageSkeleton from '../Skeleton.jsx';

// Etiketler sözlükten: `prn.station.<k>` ve `prn.kind.<k>`.
const STATIONS = ['KITCHEN', 'BAR', 'CASHIER'];
const KINDS = ['IPP', 'TCP9100', 'BLUETOOTH'];

/**
 * Yazıcılar + baskı kuyruğu.
 *
 * Sunucu yazıcıyla konuşmaz: yazıcı restoranın LAN'ında, fişi basan personel
 * cihazı (köprü kipi). Bu ekran bu yüzden "yazdır" demez — kuyruğu ve
 * yazıcı tanımlarını yönetir, ölü işi gösterir.
 */
export default function Printers() {
  const t = useT();
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [cats, setCats] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ kind: 'TCP9100', station: 'KITCHEN', paperWidth: '80', secure: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, j] = await Promise.all([
        api('/api/printing/printers'),
        api('/api/printing/jobs?limit=30'),
      ]);
      setData(d); setJobs(j);
    } catch (e) { setErr(errMessage(t, e)); }
  };
  useEffect(() => {
    load();
    // Kategori adları yazıcı kuralında gösteriliyor; `/api/menu/categories`
    // diye bir UÇ YOK (orada yalnız POST var) → 404 sessizce yutuluyordu ve
    // listede ad yerine ham kimlik çıkıyordu. Doğru kaynak `/api/menu`.
    api('/api/menu').then((d) => setCats(d.categories || [])).catch(() => {});
    // Kuyruk canlı izlenir: ölü iş fark edilmeden kalmasın
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, []);

  async function act(fn, okMsg) {
    setErr(''); setMsg(''); setBusy(true);
    try { await fn(); if (okMsg) setMsg(okMsg); await load(); }
    catch (e) { setErr(errMessage(t, e)); }
    finally { setBusy(false); }
  }

  if (err && !data) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="table" />;

  const dead = jobs.filter((j) => j.status === 'DEAD');

  return (
    <>
      {dead.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          <h2>{t('prn.deadTitle', { n: dead.length })}</h2>
          <p className="muted" style={{ marginTop: 0 }}>{t('prn.deadNote')}</p>
          {dead.map((j) => (
            <div className="row" key={j.id}>
              <span>#{j.id} · {t('prn.order')} {j.orderId ?? '—'} · {j.lastError}</span>
              <span className="spacer" />
              <button disabled={busy} onClick={() => act(
                () => api(`/api/printing/jobs/${j.id}/retry`, { method: 'POST' }), t('prn.requeued')
              )}><Icon n="refresh" />{t('prn.retry')}</button>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>{t('nav.printers')}</h2>
          <span className="badge ok">{t('prn.queued', { n: data.queue.pending })}</span>
          {data.queue.dead > 0 && <span className="badge bad">{t('prn.deadCount', { n: data.queue.dead })}</span>}
          <span className="spacer" />
          <button onClick={load} disabled={busy}><Icon n="refresh" />{t('refresh')}</button>
        </div>
        <p className="muted">{t('prn.bridgeNote', { n: data.leaseSeconds })}</p>
        <table>
          <thead>
            <tr><th>{t('name')}</th><th>{t('prn.kind')}</th><th>{t('prn.address')}</th><th>{t('prn.station')}</th><th>{t('prn.paper')}</th><th>{t('prn.categories')}</th><th>{t('status')}</th><th></th></tr>
          </thead>
          <tbody>
            {data.printers.map((p) => (
              <tr key={p.id} style={{ opacity: p.enabled ? 1 : 0.55 }}>
                <td><b>{p.name}</b></td>
                <td className="muted">{t(`prn.kind.${p.kind}`)}</td>
                <td className="muted">{p.kind === 'BLUETOOTH' ? p.btAddress : `${p.host}:${p.port ?? ''}${p.secure ? ' · TLS' : ''}`}</td>
                <td>{t(`prn.station.${p.station}`)}</td>
                <td>{p.paperWidth}</td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {Array.isArray(p.categoryIdsJson) && p.categoryIdsJson.length
                    ? p.categoryIdsJson.map((id) => cats.find((c) => c.id === id)?.name || id).join(', ')
                    : t('prn.allCategories')}
                </td>
                <td>
                  {p.lastStatus
                    ? <span className={`badge ${p.lastStatus === 'ok' ? 'ok' : 'warn'}`}>{p.lastStatus}</span>
                    : <span className="muted">{t('prn.neverPrinted')}</span>}
                </td>
                <td>
                  <button disabled={busy} onClick={() => act(
                    () => api(`/api/printing/printers/${p.id}/test`, { method: 'POST' }),
                    t('prn.testQueued')
                  )}><Icon n="printer" />{t('prn.test')}</button>
                  <button disabled={busy} onClick={() => act(
                    () => api(`/api/printing/printers/${p.id}`, { method: 'PATCH', body: { enabled: !p.enabled } })
                  )}><Icon n="power" />{p.enabled ? t('staff.disable') : t('staff.enable')}</button>
                  <button className="btn-ico" disabled={busy} aria-label={t('remove')} title={t('remove')}
                    onClick={() => confirm(t('prn.confirmDelete', { name: p.name }))
                      && act(() => api(`/api/printing/printers/${p.id}`, { method: 'DELETE' }))}>
                    <Icon n="trash" />
                  </button>
                </td>
              </tr>
            ))}
            {data.printers.length === 0 && <tr><td colSpan="8" className="muted">{t('prn.none')}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>{t('prn.add')}</h2>
        {!data.multiPrinter && data.printers.length >= 1 && (
          <p className="muted" style={{ marginTop: 0 }}>{t('prn.singleOnly')}</p>
        )}
        <div className="row">
          <input style={{ maxWidth: 150 }} placeholder={t('prn.namePlaceholder')}
            value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select style={{ maxWidth: 170 }} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`prn.kind.${k}`)}</option>)}
          </select>
          {form.kind === 'BLUETOOTH' ? (
            <input style={{ maxWidth: 170 }} placeholder="00:11:22:33:44:55"
              value={form.btAddress || ''} onChange={(e) => setForm({ ...form, btAddress: e.target.value })} />
          ) : (
            <>
              <input style={{ maxWidth: 150 }} placeholder="192.168.1.50"
                value={form.host || ''} onChange={(e) => setForm({ ...form, host: e.target.value })} />
              {/* Port ve TLS GÖRÜNÜR olmak zorunda: IPP yazıcısı 631'de düz
                  HTTP konuşur, 443'te TLS. Gizli bir varsayılan yanlış
                  seçerse cihaz bağlanamaz ve sebebi panelde görünmez. */}
              <input style={{ maxWidth: 90 }} type="number" min="1" max="65535"
                placeholder={form.kind === 'IPP' ? '631' : '9100'}
                value={form.port ?? ''}
                onChange={(e) => setForm({ ...form, port: e.target.value === '' ? undefined : Number(e.target.value) })} />
              {form.kind === 'IPP' && (
                <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!form.secure}
                    onChange={(e) => setForm({ ...form, secure: e.target.checked })} />
                  TLS (IPPS)
                </label>
              )}
            </>
          )}
          <select style={{ maxWidth: 120 }} value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })}>
            {STATIONS.map((k) => <option key={k} value={k}>{t(`prn.station.${k}`)}</option>)}
          </select>
          <select style={{ maxWidth: 100 }} value={form.paperWidth} onChange={(e) => setForm({ ...form, paperWidth: e.target.value })}>
            <option value="58">58 mm</option>
            <option value="80">80 mm</option>
            <option value="A4">A4</option>
          </select>
          <button className="primary" disabled={busy} onClick={() => act(async () => {
            if (!form.name?.trim()) throw { code: 'name_required' };
            await api('/api/printing/printers', { method: 'POST', body: form });
            setForm({ kind: 'TCP9100', station: 'KITCHEN', paperWidth: '80', secure: false });
          }, t('prn.added'))}><Icon n="plus" />{t('add')}</button>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>{t('prn.categoryNote')}</p>
      </div>

      <div className="card">
        <h2>{t('prn.recent')}</h2>
        <table>
          <thead><tr><th>#</th><th>{t('prn.order')}</th><th>{t('prn.kind')}</th><th>{t('status')}</th><th>{t('prn.attempts')}</th><th>{t('prn.error')}</th></tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td>{j.id}</td>
                <td>{j.orderId ?? '—'}</td>
                <td className="muted">{j.kind}</td>
                <td>
                  <span className={`badge ${j.status === 'DONE' ? 'ok' : j.status === 'DEAD' ? 'bad' : 'warn'}`}>
                    {j.status}
                  </span>
                </td>
                <td className="muted">{j.attempts}/{j.maxAttempts}</td>
                <td className="muted" style={{ fontSize: 12 }}>{j.lastError || '—'}</td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan="6" className="muted">{t('prn.noPrints')}</td></tr>}
          </tbody>
        </table>
      </div>

      {msg && <p className="ok">{msg}</p>}
      {err && <p className="err">{err}</p>}
    </>
  );
}
